import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { 
  useAuthStore, 
  hasRole, 
  hasPermission, 
  isSuperAdmin, 
  isAdmin, 
  isDriver 
} from '../stores/authStore';
import { User } from '../types';

/**
 * Hook principal pour la gestion de l'authentification
 * Simplifie l'utilisation du store d'authentification dans les composants
 */
export const useAuth = () => {
  const {
    user,
    token,
    isLoading,
    isAuthenticated,
    isInitialized,
    login,
    logout,
    refreshUser,
    updateUser,
  } = useAuthStore();

  const navigate = useNavigate();

  /**
   * Connexion avec gestion des erreurs (SANS redirection automatique)
   */
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await login(email, password);
      
      // Get the updated user from the store after login
      const { user: loggedInUser } = useAuthStore.getState();
      
      // Show success message but don't navigate - let the caller handle navigation
      message.success(`Bienvenue ${loggedInUser?.first_name || 'Utilisateur'} !`);
      
      // Return the user data so caller can handle navigation if needed
      return { user: loggedInUser };
    } catch (error: any) {
      message.error(error.message || 'Erreur de connexion');
      throw error;
    }
  }, [login]);

  /**
   * Déconnexion avec message de confirmation
   */
  const signOut = useCallback(() => {
    logout();
    message.info('Vous avez été déconnecté');
    navigate('/login');
  }, [logout, navigate]);

  /**
   * Rafraîchir les données utilisateur
   */
  const refresh = useCallback(async () => {
    try {
      await refreshUser();
      message.success('Données utilisateur mises à jour');
    } catch (error: any) {
      message.error('Erreur lors de la mise à jour des données');
      throw error;
    }
  }, [refreshUser]);

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   */
  const checkRole = useCallback((role: string): boolean => {
    return hasRole(user, role);
  }, [user]);

  /**
   * Vérifier si l'utilisateur a une permission spécifique
   */
  const checkPermission = useCallback((permission: string): boolean => {
    return hasPermission(user, permission);
  }, [user]);

  /**
   * Vérifier si l'utilisateur peut accéder à une ressource
   */
  const canAccess = useCallback((requiredRole?: string, requiredPermission?: string): boolean => {
    if (!isAuthenticated || !user) return false;
    
    if (requiredRole && !checkRole(requiredRole)) return false;
    if (requiredPermission && !checkPermission(requiredPermission)) return false;
    
    return true;
  }, [isAuthenticated, user, checkRole, checkPermission]);

  /**
   * Obtenir le nom d'affichage de l'utilisateur
   */
  const getDisplayName = useCallback((): string => {
    if (!user) return 'Utilisateur';
    return `${user.first_name} ${user.last_name}`.trim() || user.email;
  }, [user]);

  /**
   * Obtenir les informations de l'entreprise
   */
  const getTenant = useCallback(() => {
    return user?.tenant || null;
  }, [user]);

  /**
   * Vérifier si l'entreprise est active
   */
  const isTenantActive = useCallback((): boolean => {
    const tenant = getTenant();
    return tenant?.status === 'active';
  }, [getTenant]);

  /**
   * Obtenir l'URL de redirection par défaut selon le rôle
   */
  const getDefaultRedirectUrl = useCallback((): string => {
    if (!user) return '/login';
    
    switch (user.role) {
      case 'super_admin':
        return '/super-admin';  // ← Fixed: should be /super-admin not /dashboard
      case 'admin':
        return '/admin';
      case 'driver':
        return '/driver';
      default:
        return '/';
    }
  }, [user]);

  /**
   * Mettre à jour le profil utilisateur
   */
  const updateProfile = useCallback(async (updates: Partial<User>) => {
    try {
      updateUser(updates);
      message.success('Profil mis à jour');
    } catch (error) {
      message.error('Erreur lors de la mise à jour du profil');
      throw error;
    }
  }, [updateUser]);

  return {
    // États
    user,
    token,
    isLoading,
    isAuthenticated,
    isInitialized,
    
    // Actions
    signIn,
    signOut,
    refresh,
    updateProfile,
    
    // Vérifications de rôles et permissions
    checkRole,
    checkPermission,
    canAccess,
    isSuperAdmin: isSuperAdmin(user),
    isAdmin: isAdmin(user),
    isDriver: isDriver(user),
    
    // Informations utiles
    getDisplayName,
    getTenant,
    isTenantActive,
    getDefaultRedirectUrl,
    
    // Informations de l'entreprise
    tenant: getTenant(),
    tenantName: getTenant()?.name,
    tenantStatus: getTenant()?.status,
  };
};

/**
 * Hook pour vérifier l'autorisation d'accès à une page/ressource
 * Redirige automatiquement si l'utilisateur n'a pas les droits
 */
export const useAuthGuard = (
  requiredRole?: string,
  requiredPermission?: string,
  redirectTo: string = '/login'
) => {
  const { isAuthenticated, user, checkRole, checkPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifier l'authentification
    if (!isAuthenticated) {
      navigate(redirectTo);
      return;
    }

    // Vérifier le rôle requis
    if (requiredRole && !checkRole(requiredRole)) {
      navigate('/403'); // Page d'erreur 403
      return;
    }

    // Vérifier la permission requise
    if (requiredPermission && !checkPermission(requiredPermission)) {
      navigate('/403'); // Page d'erreur 403
      return;
    }

    // Vérifier que l'entreprise est active (sauf pour super admin)
    if (user?.role !== 'super_admin' && user?.tenant?.status !== 'active') {
      navigate('/tenant-inactive'); // Page d'erreur entreprise inactive
      return;
    }
  }, [
    isAuthenticated,
    user,
    requiredRole,
    requiredPermission,
    redirectTo,
    navigate,
    checkRole,
    checkPermission,
  ]);

  const hasAccess = isAuthenticated &&
    (!requiredRole || checkRole(requiredRole)) &&
    (!requiredPermission || checkPermission(requiredPermission)) &&
    (user?.role === 'super_admin' || user?.tenant?.status === 'active');

  return {
    hasAccess,
    isLoading: false,
  };
};

/**
 * Hook pour les formulaires de connexion
 * Gère l'état du formulaire et la validation
 */
export const useLoginForm = () => {
  const { signIn, isLoading } = useAuth();

  const handleSubmit = useCallback(async (values: { email: string; password: string }) => {
    try {
      await signIn(values.email, values.password);
    } catch (error) {
      // L'erreur est déjà gérée dans signIn
      throw error;
    }
  }, [signIn]);

  return {
    handleSubmit,
    isLoading,
  };
};

/**
 * Hook pour l'initialisation de l'authentification au démarrage de l'app
 */
export const useAuthInitialization = () => {
  const { isAuthenticated, token, refresh } = useAuth();

  useEffect(() => {
    // Si un token existe au démarrage, vérifier qu'il est toujours valide
    if (token && isAuthenticated) {
      refresh();
    }
  }, []); // Exécuter seulement au montage du composant

  return {
    isInitialized: true,
  };
};